import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  FileText, Search, Download, Eye, RefreshCw, Filter, 
  ChevronLeft, ChevronRight, Calendar, User, FileDown, FileSignature, Send
} from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentPolicyContracts() {
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Preview state
  const [previewContract, setPreviewContract] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Generate contract state
  const [generatingContract, setGeneratingContract] = useState(null);
  
  // E-Signature state
  const [sendingForSignature, setSendingForSignature] = useState(null);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchContracts();
    fetchStats();
  }, [page, statusFilter]);

  const fetchContracts = async () => {
    setLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20"
      });
      if (search) params.append("search", search);
      if (statusFilter) params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/agent/policies/contracts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data.contracts);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/policies/contracts/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchContracts();
  };

  const handlePreview = async (contractId) => {
    setLoadingPreview(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/policies/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewContract(response.data);
    } catch (error) {
      toast.error("Failed to load contract details");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async (policyId, format = "pdf") => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/policies/${policyId}/contract/download?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TheRentPayer_Contract_${policyId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Contract downloaded successfully");
    } catch (error) {
      toast.error("Failed to download contract");
    }
  };

  const handleGenerateContract = async (policyId) => {
    setGeneratingContract(policyId);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/policies/${policyId}/generate-contract`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate contract");
    } finally {
      setGeneratingContract(null);
    }
  };

  const handleSendForSignature = async (policyId) => {
    setSendingForSignature(policyId);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/policies/${policyId}/send-for-signature`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send for signature");
    } finally {
      setSendingForSignature(null);
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === contracts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(contracts.map(c => c.policy_id));
    }
  };

  const toggleSelect = (policyId) => {
    setSelectedIds(prev => 
      prev.includes(policyId) 
        ? prev.filter(id => id !== policyId)
        : [...prev, policyId]
    );
  };

  const isAllSelected = contracts.length > 0 && selectedIds.length === contracts.length;
  const isSomeSelected = selectedIds.length > 0;

  // Bulk actions
  const handleBulkExport = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/policies/bulk`,
        { ids: selectedIds, action: "export" },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `policies_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported ${selectedIds.length} policies`);
      setSelectedIds([]);
    } catch (error) {
      toast.error("Failed to export policies");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkSendForSignature = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/policies/bulk`,
        { ids: selectedIds, action: "send_for_signature" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Sent ${response.data.success} policies for signature`);
      if (response.data.failed > 0) {
        toast.warning(`Failed for ${response.data.failed} policies`);
      }
      setSelectedIds([]);
    } catch (error) {
      toast.error("Failed to send for signature");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkRegenerate = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/policies/bulk`,
        { ids: selectedIds, action: "regenerate_contracts" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Regenerated ${response.data.success} contracts`);
      setSelectedIds([]);
      fetchContracts();
    } catch (error) {
      toast.error("Failed to regenerate contracts");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800",
      expired: "bg-gray-100 text-gray-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return <Badge className={styles[status] || "bg-blue-100 text-blue-800"}>{status || "N/A"}</Badge>;
  };

  return (
    <AgentLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
          Policy Contracts
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage and view all policy contracts
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="card-organic">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_contracts}</p>
                  <p className="text-xs text-muted-foreground">Total Contracts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <NestIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_policies}</p>
                  <p className="text-xs text-muted-foreground">Active Policies</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-gray-100">
                  <Calendar className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.expired_policies}</p>
                  <p className="text-xs text-muted-foreground">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100">
                  <FileText className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.cancelled_policies}</p>
                  <p className="text-xs text-muted-foreground">Cancelled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-organic">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100">
                  <FileDown className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.contracts_last_30_days}</p>
                  <p className="text-xs text-muted-foreground">Last 30 Days</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filter */}
      <Card className="card-organic mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by policy ID, name, email, or account number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-contracts-input"
                />
              </div>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(value) => { setStatusFilter(value === "all" ? "" : value); setPage(1); }}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="search-btn">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            <Button type="button" variant="outline" onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); fetchContracts(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {isSomeSelected && (
        <div className="mb-4 flex flex-wrap items-center gap-2 bg-primary/5 px-4 py-3 rounded-lg border border-primary/20">
          <Badge variant="secondary" className="mr-2">
            {selectedIds.length} selected
          </Badge>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleBulkExport}
            disabled={bulkActionLoading}
            data-testid="bulk-export-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleBulkSendForSignature}
            disabled={bulkActionLoading}
            data-testid="bulk-esign-btn"
          >
            <FileSignature className="h-4 w-4 mr-1" />
            Send for Signature
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleBulkRegenerate}
            disabled={bulkActionLoading}
            data-testid="bulk-regenerate-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Regenerate
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => setSelectedIds([])}
          >
            Clear
          </Button>
        </div>
      )}

      {/* Contracts Table */}
      <Card className="card-organic">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
            Contracts ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No contracts found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium w-12">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="text-left p-3 font-medium">Policy ID</th>
                      <th className="text-left p-3 font-medium">Account #</th>
                      <th className="text-left p-3 font-medium">Customer</th>
                      <th className="text-left p-3 font-medium">Monthly Rent</th>
                      <th className="text-left p-3 font-medium">Premium</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Created</th>
                      <th className="text-left p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => (
                      <tr key={contract.contract_id} className={`border-b hover:bg-muted/50 ${selectedIds.includes(contract.policy_id) ? "bg-primary/5" : ""}`}>
                        <td className="p-3">
                          <Checkbox
                            checked={selectedIds.includes(contract.policy_id)}
                            onCheckedChange={() => toggleSelect(contract.policy_id)}
                            data-testid={`select-${contract.policy_id}`}
                          />
                        </td>
                        <td className="p-3">
                          <code className="text-sm bg-muted px-2 py-1 rounded">{contract.policy_id}</code>
                        </td>
                        <td className="p-3">
                          <code className="text-sm text-primary font-medium">{contract.account_number || "N/A"}</code>
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{contract.user_name || "N/A"}</p>
                            <p className="text-sm text-muted-foreground">{contract.user_email}</p>
                          </div>
                        </td>
                        <td className="p-3">${contract.monthly_rent?.toFixed(2) || "N/A"}</td>
                        <td className="p-3">${contract.monthly_premium?.toFixed(2) || "N/A"}/mo</td>
                        <td className="p-3">{getStatusBadge(contract.policy_status)}</td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {contract.created_at ? new Date(contract.created_at).toLocaleDateString() : "N/A"}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handlePreview(contract.contract_id)}
                                  data-testid={`preview-${contract.contract_id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Contract Preview</DialogTitle>
                                </DialogHeader>
                                {loadingPreview ? (
                                  <div className="flex items-center justify-center h-64">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                  </div>
                                ) : previewContract ? (
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                                      <div>
                                        <p className="text-sm text-muted-foreground">Policy ID</p>
                                        <p className="font-medium">{previewContract.policy?.policy_id}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-muted-foreground">Account Number</p>
                                        <p className="font-medium">{previewContract.user?.account_number || "N/A"}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-muted-foreground">Customer</p>
                                        <p className="font-medium">{previewContract.user?.full_name}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-muted-foreground">Email</p>
                                        <p className="font-medium">{previewContract.user?.email}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-muted-foreground">Monthly Rent</p>
                                        <p className="font-medium">${previewContract.policy?.monthly_rent?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-muted-foreground">Monthly Premium</p>
                                        <p className="font-medium">${previewContract.policy?.monthly_premium?.toFixed(2)}</p>
                                      </div>
                                    </div>
                                    
                                    <div className="border rounded-lg overflow-hidden">
                                      <iframe
                                        srcDoc={previewContract.contract?.contract_html}
                                        className="w-full h-[500px]"
                                        title="Contract Preview"
                                      />
                                    </div>
                                    
                                    <div className="flex gap-2 justify-end">
                                      <Button
                                        variant="outline"
                                        onClick={() => handleDownload(previewContract.policy?.policy_id, "html")}
                                      >
                                        <Download className="h-4 w-4 mr-2" />
                                        Download HTML
                                      </Button>
                                      <Button
                                        variant="outline"
                                        onClick={() => handleDownload(previewContract.policy?.policy_id, "pdf")}
                                      >
                                        <Download className="h-4 w-4 mr-2" />
                                        Download PDF
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          handleSendForSignature(previewContract.policy?.policy_id);
                                        }}
                                        disabled={sendingForSignature === previewContract.policy?.policy_id}
                                        data-testid="send-for-signature-btn"
                                      >
                                        {sendingForSignature === previewContract.policy?.policy_id ? (
                                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                          <FileSignature className="h-4 w-4 mr-2" />
                                        )}
                                        Send for Signature
                                      </Button>
                                    </div>
                                  </div>
                                ) : null}
                              </DialogContent>
                            </Dialog>
                            
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDownload(contract.policy_id, "pdf")}
                              data-testid={`download-${contract.contract_id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            
                            <Button 
                              size="sm"
                              variant="secondary"
                              onClick={() => handleGenerateContract(contract.policy_id)}
                              disabled={generatingContract === contract.policy_id}
                              data-testid={`regenerate-${contract.contract_id}`}
                            >
                              {generatingContract === contract.policy_id ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4" />
                              )}
                            </Button>
                            
                            {contract.policy_status === "active" && (
                              <Button 
                                size="sm"
                                onClick={() => handleSendForSignature(contract.policy_id)}
                                disabled={sendingForSignature === contract.policy_id}
                                data-testid={`esign-${contract.contract_id}`}
                                className="bg-primary hover:bg-primary/90"
                              >
                                {sendingForSignature === contract.policy_id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <FileSignature className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </AgentLayout>
  );
}
